/**
 * Integrytas Group - Base App (MOBILE)
 * Page: ODS.SYNC — mobile-native.
 *
 * Touch-first counterpart of src/pages/admin/OdsSyncManagerPage.tsx (Offline Data
 * Sync manager). A DISTINCT presentation — KPI tiles + dense card list + facet
 * chips + bottom-sheet detail + bottom-sheet editor / register — over the SAME
 * data/logic layer as the desktop page:
 *   - reads via apiDb from ods_sync_entity (the sync registry),
 *   - every write routes through the Common Routine (routedInsert / routedUpdate)
 *     — §2 single write path; never a direct apiDb write.
 * It never re-hosts the desktop grid; §16.
 *
 * Scope note (mirrors the desktop page): attaching the CDC trigger is a DBA action
 * (SELECT ods_enable_sync('entity','pk_col','STRATEGY')); this surface manages the
 * registry entries and their conflict strategy for already-registered entities.
 * The strategy/tiebreak values are CHECK-enums (§17.B11) — the stored value stays
 * the code; only the displayed label is localized through t().
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Inbox, Power, X, Plus, SquarePen, RefreshCw } from 'lucide-react';
import { apiDb } from '../../lib/api_client';
import { routedInsert, routedUpdate } from '../../lib/routed_writes';
import { MobilePageHeader, MobileStatStrip, MobileContentCard, MobileCardRow, MobileDetailRow, MobileFilterChips, MobileStatusPill } from '../components';
import type { AuthSession } from '../../types/auth';
import type { MobilePageComponentProps } from '../mobile_page_components';
import { hasAccess } from '../../types/auth';

const PAGE_CODE = 'ODS.SYNC';

/* ── Types & CHECK-enum option constants (§17.B11) ───────────────────────────── */

type Strategy = 'LAST_WRITE_WINS' | 'FIELD_MERGE' | 'USER_RECONCILE';
type Tiebreak = 'LATEST' | 'SERVER' | 'CLIENT';

interface EntityRow {
  ods_sync_entity_id: number;
  entity_name: string;
  pk_column: string;
  strategy: Strategy;
  lww_tiebreak: Tiebreak;
  is_active: 'Y' | 'N';
  description: string | null;
}

/** CHECK-enum option: stored value stays the code; only the label is localized. */
interface EnumOpt { value: string; labelKey: string; fallback: string }

const STRATEGY_OPTS: EnumOpt[] = [
  { value: 'LAST_WRITE_WINS', labelKey: 'mobile.ods.strategy.LAST_WRITE_WINS', fallback: 'Last write wins' },
  { value: 'FIELD_MERGE', labelKey: 'mobile.ods.strategy.FIELD_MERGE', fallback: 'Field merge' },
  { value: 'USER_RECONCILE', labelKey: 'mobile.ods.strategy.USER_RECONCILE', fallback: 'User reconcile' },
];

const TIEBREAK_OPTS: EnumOpt[] = [
  { value: 'LATEST', labelKey: 'mobile.ods.tiebreak.LATEST', fallback: 'Latest' },
  { value: 'SERVER', labelKey: 'mobile.ods.tiebreak.SERVER', fallback: 'Server' },
  { value: 'CLIENT', labelKey: 'mobile.ods.tiebreak.CLIENT', fallback: 'Client' },
];

/** Curry a code→localized-label resolver for a CHECK-enum. */
function makeEnumLabel(opts: EnumOpt[], t: (k: string, d: string) => string): (v: string) => string {
  return (v: string) => {
    const o = opts.find((x) => x.value === v);
    return o ? t(o.labelKey, o.fallback) : v;
  };
}

type StatusFacet = 'all' | 'active' | 'inactive';

/* ══════════════════════════════════════════════════════════════════════════ */
/*  List screen                                                                 */
/* ══════════════════════════════════════════════════════════════════════════ */

export function MobileOdsSyncManagerPage({ session }: MobilePageComponentProps) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canAdd = hasAccess(session, PAGE_CODE, 'add');
  const canChange = hasAccess(session, PAGE_CODE, 'change');

  const { t } = useTranslation();
  const [rows, setRows] = useState<EntityRow[]>([]);
  const [filter, setFilter] = useState('');
  const [statusFacet, setStatusFacet] = useState<StatusFacet>('active');
  const [strategyFacet, setStrategyFacet] = useState<string>('all');
  const [selected, setSelected] = useState<EntityRow | null>(null);
  const [editing, setEditing] = useState<EntityRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const strategyLabel = makeEnumLabel(STRATEGY_OPTS, t);
  const tiebreakLabel = makeEnumLabel(TIEBREAK_OPTS, t);

  const reload = async () => {
    setBusy(true);
    setError(null);
    try {
      const { data, error: e } = await apiDb
        .from('ods_sync_entity')
        .select('ods_sync_entity_id, entity_name, pk_column, strategy, lww_tiebreak, is_active, description')
        .order('entity_name');
      if (e) throw e;
      setRows((data ?? []) as EntityRow[]);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { void reload(); }, []);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFacet === 'active' && r.is_active !== 'Y') return false;
      if (statusFacet === 'inactive' && r.is_active !== 'N') return false;
      if (strategyFacet !== 'all' && r.strategy !== strategyFacet) return false;
      if (!q) return true;
      return r.entity_name.toLowerCase().includes(q) || r.pk_column.toLowerCase().includes(q);
    });
  }, [rows, filter, statusFacet, strategyFacet]);

  // KPI summary derived during render (no effect) — total / active / inactive.
  const kpis = useMemo(() => {
    const active = rows.filter((r) => r.is_active === 'Y').length;
    return { total: rows.length, active, inactive: rows.length - active };
  }, [rows]);

  const statusFacets: { key: StatusFacet; label: string }[] = [
    { key: 'all', label: t('mobile.ods.all', 'All') },
    { key: 'active', label: t('common.boolean.active', 'Active') },
    { key: 'inactive', label: t('common.boolean.inactive', 'Inactive') },
  ];

  const toggleActive = async (r: EntityRow) => {
    setActionBusy(true);
    setError(null);
    const next = r.is_active === 'Y' ? 'N' : 'Y';
    // §2: route through the Common Routine (forensic stamp + audit + hooks).
    const { error: e } = await routedUpdate(session, 'ods_sync_entity', 'ods_sync_entity_id', r.ods_sync_entity_id, { is_active: next });
    setActionBusy(false);
    if (e) { setError(e.message); return; }
    setSelected(null);
    void reload();
  };

  const strategyChips = [
    { value: 'all', label: t('mobile.ods.all_strategies', 'All strategies') },
    ...STRATEGY_OPTS.map((o) => ({ value: o.value, label: t(o.labelKey, o.fallback) })),
  ];

  return (
    <section className="m-screen">
      <MobilePageHeader
        title={t('mobile.ods.title', 'Offline Sync')}
        description={t('mobile.ods.description', 'Entities replicated for offline use and how their conflicts resolve.')}
      />

      <MobileStatStrip
        stats={[
          { value: kpis.total, label: t('mobile.ods.kpi.total', 'Entities') },
          { value: kpis.active, label: t('mobile.ods.kpi.active', 'Sync on') },
          { value: kpis.inactive, label: t('mobile.ods.kpi.inactive', 'Sync off') },
        ]}
      />

      <div className="m-searchbox">
        <Search />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('mobile.ods.search', 'Search entities')}
          aria-label={t('mobile.ods.search', 'Search entities')}
        />
      </div>

      <MobileFilterChips
        chips={statusFacets.map((f) => ({ value: f.key, label: f.label }))}
        isActive={(v) => statusFacet === v}
        onSelect={(v) => setStatusFacet(v as StatusFacet)}
      />

      <MobileFilterChips
        chips={strategyChips}
        isActive={(v) => strategyFacet === v}
        onSelect={(v) => setStrategyFacet(v)}
      />

      {canAdd && (<button
        type="button"
        className="m-btn m-btn-primary"
        style={{ margin: '6px 0 4px' }}
        onClick={() => setCreating(true)}
      >
        <Plus size={16} /> {t('mobile.ods.register', 'Register entity')}
      </button>)}

      {error && (
        <MobileContentCard>
          <div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div>
        </MobileContentCard>
      )}

      {filtered.length === 0 && !busy ? (
        <MobileContentCard><div className="m-empty"><Inbox /> {t('mobile.ods.none', 'No sync entities match')}</div></MobileContentCard>
      ) : (
        <MobileContentCard list>
          {filtered.map((r) => (
            <MobileCardRow
              key={r.ods_sync_entity_id}
              lead={<RefreshCw />}
              leadTone={r.is_active === 'Y' ? 'green' : 'slate'}
              title={r.entity_name}
              subtitle={`${r.pk_column} · ${strategyLabel(r.strategy)}`}
              trailing={
                <MobileStatusPill tone={r.is_active === 'Y' ? 'ok' : 'no'}>
                  {r.is_active === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}
                </MobileStatusPill>
              }
              chevron
              onClick={() => setSelected(r)}
            />
          ))}
        </MobileContentCard>
      )}

      {/* Detail bottom-sheet */}
      <div className={'m-scrim' + (selected ? ' m-open' : '')} onClick={() => { if (!actionBusy) setSelected(null); }} />
      <div className={'m-sheet' + (selected ? ' m-open' : '')} aria-label={t('mobile.ods.detail', 'Sync entity detail')}>
        <div className="m-grabber" />
        {selected && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><RefreshCw size={20} /></span>
              <span>
                <div className="m-nm">{selected.entity_name}</div>
                <div className="m-em">{selected.pk_column}</div>
              </span>
            </div>
            <MobileContentCard>
              <MobileDetailRow
                label={t('mobile.ods.status', 'Sync')}
                value={
                  <MobileStatusPill tone={selected.is_active === 'Y' ? 'ok' : 'no'}>
                    {selected.is_active === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}
                  </MobileStatusPill>
                }
              />
              <MobileDetailRow label={t('mobile.ods.strategy_lbl', 'Strategy')} value={strategyLabel(selected.strategy)} />
              <MobileDetailRow label={t('mobile.ods.tiebreak_lbl', 'LWW tiebreak')} value={tiebreakLabel(selected.lww_tiebreak)} />
              {selected.description && (
                <MobileDetailRow label={t('mobile.ods.description', 'Description')} value={selected.description} />
              )}
            </MobileContentCard>
            <div className="m-actionbar">
              {canChange && (<button
                type="button"
                className="m-btn m-btn-primary"
                disabled={actionBusy}
                onClick={() => { setEditing(selected); setSelected(null); }}
              >
                <SquarePen size={16} /> {t('mobile.ods.edit', 'Edit')}
              </button>)}
              {canChange && (<button
                type="button"
                className={'m-btn ' + (selected.is_active === 'Y' ? 'm-btn-danger' : 'm-btn-ghost')}
                disabled={actionBusy}
                onClick={() => void toggleActive(selected)}
              >
                <Power size={16} />
                {selected.is_active === 'Y' ? t('users.list.actions.deactivate', 'Deactivate') : t('users.list.actions.activate', 'Activate')}
              </button>)}
              <button type="button" className="m-btn m-btn-ghost" disabled={actionBusy} onClick={() => setSelected(null)} aria-label={t('mobile.close', 'Close')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>

      {/* Edit strategy / tiebreak / active */}
      <OdsEntityEditSheet
        session={session}
        row={editing}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); void reload(); }}
      />

      {/* Register a new entity */}
      <OdsEntityRegisterSheet
        session={session}
        open={creating}
        onClose={() => setCreating(false)}
        onSaved={() => { setCreating(false); void reload(); }}
      />
    </section>
  );
}

/* ══════════════════════════════════════════════════════════════════════════ */
/*  Edit sheet — strategy + tiebreak + active, saved via the Common Routine.    */
/*  Mirrors the desktop save (strategy, lww_tiebreak, is_active). Entity name    */
/*  and PK column are the registry identity and shown read-only.                 */
/* ══════════════════════════════════════════════════════════════════════════ */

interface OdsEntityEditSheetProps {
  session: AuthSession;
  row: EntityRow | null;
  onClose: () => void;
  onSaved: () => void;
}

function OdsEntityEditSheet({ session, row, onClose, onSaved }: OdsEntityEditSheetProps) {
  const { t } = useTranslation();
  const [strategy, setStrategy] = useState<Strategy>('LAST_WRITE_WINS');
  const [tiebreak, setTiebreak] = useState<Tiebreak>('LATEST');
  const [active, setActive] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Seed the form whenever a different row is opened.
  useEffect(() => {
    if (!row) return;
    setStrategy(row.strategy);
    setTiebreak(row.lww_tiebreak);
    setActive(row.is_active === 'Y');
    setError(null);
  }, [row]);

  const save = async () => {
    if (!row) return;
    setBusy(true);
    setError(null);
    // §2: route through the Common Routine (forensic stamp + audit + hooks).
    const { error: e } = await routedUpdate(session, 'ods_sync_entity', 'ods_sync_entity_id', row.ods_sync_entity_id, {
      strategy,
      lww_tiebreak: tiebreak,
      is_active: active ? 'Y' : 'N',
    });
    setBusy(false);
    if (e) { setError(e.message); return; }
    onSaved();
  };

  const open = row !== null;

  return (
    <>
      <div className={'m-scrim' + (open ? ' m-open' : '')} onClick={() => { if (!busy) onClose(); }} />
      <div className={'m-sheet' + (open ? ' m-open' : '')} aria-label={t('mobile.ods.edit_sheet', 'Edit sync entity')}>
        <div className="m-grabber" />
        {row && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><RefreshCw size={20} /></span>
              <span>
                <div className="m-nm">{t('mobile.ods.edit_title', 'Edit sync entity')}</div>
                <div className="m-em">{row.entity_name}</div>
              </span>
            </div>

            <div className="m-fieldcard">
              <div className="m-formrow">
                <label>{t('mobile.ods.entity', 'Entity')}</label>
                <div className="m-formrow-ro">
                  {row.entity_name}
                  <span className="m-formrow-hint"> · {row.pk_column}</span>
                </div>
              </div>
              <div className="m-formrow">
                <label htmlFor="m-ods-strategy">{t('mobile.ods.strategy_lbl', 'Strategy')}</label>
                <select id="m-ods-strategy" className="m-sel" value={strategy} onChange={(e) => setStrategy(e.target.value as Strategy)}>
                  {STRATEGY_OPTS.map((o) => <option key={o.value} value={o.value}>{t(o.labelKey, o.fallback)}</option>)}
                </select>
              </div>
              <div className="m-formrow">
                <label htmlFor="m-ods-tiebreak">{t('mobile.ods.tiebreak_lbl', 'LWW tiebreak')}</label>
                <select id="m-ods-tiebreak" className="m-sel" value={tiebreak} onChange={(e) => setTiebreak(e.target.value as Tiebreak)}>
                  {TIEBREAK_OPTS.map((o) => <option key={o.value} value={o.value}>{t(o.labelKey, o.fallback)}</option>)}
                </select>
              </div>
              <div className="m-formrow m-formrow-inline">
                <span>
                  <div className="m-formrow-l">{t('common.boolean.active', 'Active')}</div>
                  <div className="m-formrow-s">{t('mobile.ods.active_hint', 'Sync this entity offline')}</div>
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={active}
                  className={'m-switch ' + (active ? 'm-on' : 'm-off')}
                  onClick={() => setActive((v) => !v)}
                >
                  <span className="m-knob" />
                </button>
              </div>
            </div>

            {error && <div className="m-card"><div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div></div>}

            <div className="m-actionbar">
              <button type="button" className="m-btn m-btn-primary" disabled={busy} onClick={() => void save()}>
                {busy ? t('common.saving', 'Saving…') : t('common.save', 'Save changes')}
              </button>
              <button type="button" className="m-btn m-btn-ghost" disabled={busy} onClick={onClose} aria-label={t('common.cancel', 'Cancel')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}

/* ══════════════════════════════════════════════════════════════════════════ */
/*  Register sheet — add a registry entry (entity + PK column + strategy).       */
/*  New entries start inactive; a DBA then attaches the CDC trigger with          */
/*  SELECT ods_enable_sync('entity','pk','STRATEGY'). Mirrors the desktop add.    */
/* ══════════════════════════════════════════════════════════════════════════ */

interface OdsEntityRegisterSheetProps {
  session: AuthSession;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}

function OdsEntityRegisterSheet({ session, open, onClose, onSaved }: OdsEntityRegisterSheetProps) {
  const { t } = useTranslation();
  const [entityName, setEntityName] = useState('');
  const [pkColumn, setPkColumn] = useState('');
  const [strategy, setStrategy] = useState<Strategy>('LAST_WRITE_WINS');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset the form each time the sheet is opened.
  useEffect(() => {
    if (!open) return;
    setEntityName('');
    setPkColumn('');
    setStrategy('LAST_WRITE_WINS');
    setError(null);
  }, [open]);

  const save = async () => {
    if (!entityName.trim() || !pkColumn.trim()) {
      setError(t('mobile.ods.err.entity_pk_required', 'Entity and PK column are required.'));
      return;
    }
    setBusy(true);
    setError(null);
    // §2: route through the Common Routine. New registrations start inactive until
    // a DBA attaches the CDC trigger.
    const { error: e } = await routedInsert(session, 'ods_sync_entity', {
      entity_name: entityName.trim(),
      pk_column: pkColumn.trim(),
      strategy,
      is_active: 'N',
    });
    setBusy(false);
    if (e) { setError(e.message); return; }
    onSaved();
  };

  return (
    <>
      <div className={'m-scrim' + (open ? ' m-open' : '')} onClick={() => { if (!busy) onClose(); }} />
      <div className={'m-sheet' + (open ? ' m-open' : '')} aria-label={t('mobile.ods.register_sheet', 'Register sync entity')}>
        <div className="m-grabber" />
        {open && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><Plus size={20} /></span>
              <span>
                <div className="m-nm">{t('mobile.ods.register', 'Register entity')}</div>
                <div className="m-em">{t('mobile.ods.registry', 'Offline sync registry')}</div>
              </span>
            </div>

            <div className="m-fieldcard">
              <div className="m-formrow">
                <label htmlFor="m-ods-entity">{t('mobile.ods.entity', 'Entity')}</label>
                <input
                  id="m-ods-entity"
                  className="m-inp"
                  value={entityName}
                  onChange={(e) => setEntityName(e.target.value)}
                  placeholder="csm_user_master"
                  autoFocus
                />
              </div>
              <div className="m-formrow">
                <label htmlFor="m-ods-pk">{t('mobile.ods.pk_column', 'PK column')}</label>
                <input
                  id="m-ods-pk"
                  className="m-inp"
                  value={pkColumn}
                  onChange={(e) => setPkColumn(e.target.value)}
                  placeholder="user_id"
                />
              </div>
              <div className="m-formrow">
                <label htmlFor="m-ods-new-strategy">{t('mobile.ods.strategy_lbl', 'Strategy')}</label>
                <select id="m-ods-new-strategy" className="m-sel" value={strategy} onChange={(e) => setStrategy(e.target.value as Strategy)}>
                  {STRATEGY_OPTS.map((o) => <option key={o.value} value={o.value}>{t(o.labelKey, o.fallback)}</option>)}
                </select>
              </div>
              <div className="m-formrow">
                <label>{t('mobile.ods.dba_step', 'Next step')}</label>
                <div className="m-formrow-ro">
                  {t('mobile.ods.dba_hint', 'A DBA attaches the CDC trigger to start syncing.')}
                </div>
              </div>
            </div>

            {error && <div className="m-card"><div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div></div>}

            <div className="m-actionbar">
              <button type="button" className="m-btn m-btn-primary" disabled={busy} onClick={() => void save()}>
                {busy ? t('common.saving', 'Saving…') : t('mobile.ods.register', 'Register entity')}
              </button>
              <button type="button" className="m-btn m-btn-ghost" disabled={busy} onClick={onClose} aria-label={t('common.cancel', 'Cancel')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
